import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
    journalEntryOpenPath,
    parseLedgerBasis,
    parsePositiveInteger,
    transactionOpenPath,
} from "./productEntityOpenPaths";

export const ProductEntityOpenRoute: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useStateAwareNavigate();
    const { organizationId, workspaceId, initializeScope } = useScope();

    useEffect(() => {
        const targetWorkspaceId = searchParams.get("workspaceId");
        if (targetWorkspaceId && targetWorkspaceId !== workspaceId) {
            initializeScope({
                organizationId,
                workspaceId: targetWorkspaceId,
                source: OrganizationMemberClientAccessSource.HYDRATE,
            });
            return;
        }

        const target = searchParams.get("target");
        if (target === "transaction") {
            const transactionId = parsePositiveInteger(
                searchParams.get("transactionId"),
            );
            if (!transactionId) {
                navigate("/", { replace: true });
                return;
            }
            navigate(transactionOpenPath(transactionId), { replace: true });
            return;
        }

        if (target === "journalEntry") {
            const entityId = parsePositiveInteger(searchParams.get("entityId"));
            const journalEntryId = searchParams.get("journalEntryId");
            if (!entityId || !journalEntryId) {
                navigate("/", { replace: true });
                return;
            }

            const ledgerBasis = parseLedgerBasis(
                searchParams.get("ledgerBasis"),
            );
            navigate(
                journalEntryOpenPath({
                    entityId,
                    journalEntryId,
                    ledgerBasis,
                    workspaceId: targetWorkspaceId ?? undefined,
                }),
                { replace: true },
            );
            return;
        }

        if (target === "work") {
            const closeId = searchParams.get("closeId");
            const entityId = searchParams.get("entityId");
            const view = searchParams.get("view");
            const params: Record<string, string> = {};
            if (closeId) {
                params.closeId = closeId;
            }
            if (entityId) {
                params.entityId = entityId;
            }
            if (view) {
                params.view = view;
            }
            const destination =
                Object.keys(params).length > 0
                    ? `/?${new URLSearchParams(params).toString()}`
                    : "/";
            navigate(destination, { replace: true });
            return;
        }

        if (target === "task") {
            const taskId = searchParams.get("taskId");
            if (!taskId) {
                navigate("/", { replace: true });
                return;
            }
            const params = new URLSearchParams({ taskId });
            if (searchParams.get("panel") === "chat") {
                params.set("taskPanel", "chat");
            }
            navigate(`/?${params.toString()}`, { replace: true });
            return;
        }

        if (target === "chartOfAccounts") {
            const entityId = parsePositiveInteger(searchParams.get("entityId"));
            navigate(
                entityId
                    ? `/chart-of-accounts?entityId=${entityId}`
                    : "/chart-of-accounts",
                { replace: true },
            );
            return;
        }

        navigate("/", { replace: true });
    }, [initializeScope, navigate, organizationId, searchParams, workspaceId]);

    return null;
};
